import { useEffect, useMemo, useState } from "react";
import {
  fetchFPLData,
  fetchEliminationRules,
  fetchEliminationCandidates,
  confirmEliminations,
  fetchEliminatedSoFar,
  adminPing,
  fetchEliminationAdminStatus,
  autoFinalizeElimination,
} from "../api/fplApi";

const Spinner = ({ size = 18, message = "Loading..This might take few seconds." }) => (
  <div className="flex items-center gap-2 text-gray-600">
    <div
      className="animate-spin rounded-full border-2 border-gray-300 border-t-gray-900"
      style={{ width: size, height: size }}
      aria-hidden="true"
    />
    <span className="text-sm">{message}</span>
  </div>
);

/**
 * Elimination League Admin
 * - Defaults to Classic League ID 136323
 * - Detects latest finished GW automatically (can be overridden)
 * - Loads candidates (excludes previously eliminated)
 * - Preselects bottom N based on Supabase rules for the GW
 * - Automatic finalization writes eliminations after FPL confirms a GW
 * - Manual selection remains available as an admin repair/override
 */
export default function EliminationAdmin() {
  // --- Admin token gate ---
  const [adminKey, setAdminKey] = useState(localStorage.getItem("admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  // --- Controls & data ---
  const [leagueId, setLeagueId] = useState("136323");
  const [gameweek, setGameweek] = useState(""); // let auto-detect fill this
  const [latestFinished, setLatestFinished] = useState(null);

  const [net, setNet] = useState(true);
  const [rows, setRows] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const selectedCount = selected.size;
  const [loading, setLoading] = useState(false);

  const [ruleCount, setRuleCount] = useState(0);
  const [eliminatedSoFar, setEliminatedSoFar] = useState([]);
  const [autoStatus, setAutoStatus] = useState(null);
  const [autoRunning, setAutoRunning] = useState(false);

  // ---- Token verification (server-verified) ----
  // Verify the token already saved in localStorage once when the page opens.
  // Do NOT depend on adminKey here: doing so re-rendered the whole gate on every
  // keystroke and caused the password input to lose focus after one character.
  useEffect(() => {
    let active = true;
    (async () => {
      const storedKey = (localStorage.getItem("admin_key") || "").trim();
      if (!storedKey) {
        if (active) {
          setAuthed(false);
          setChecking(false);
        }
        return;
      }

      setChecking(true);
      try {
        const ok = await adminPing();
        if (active) setAuthed(ok);
      } catch {
        if (active) setAuthed(false);
      } finally {
        if (active) setChecking(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const saveAdminKey = async () => {
    const key = adminKey.trim();
    if (!key) return;

    localStorage.setItem("admin_key", key);
    setChecking(true);
    try {
      const ok = await adminPing();
      setAuthed(ok);
      if (!ok) {
        localStorage.removeItem("admin_key");
        alert("Invalid admin key");
      }
    } catch {
      setAuthed(false);
      localStorage.removeItem("admin_key");
      alert("Unable to verify admin key");
    } finally {
      setChecking(false);
    }
  };

  // --- Auto-detect latest finished GW ---
  useEffect(() => {
    (async () => {
      try {
        const boot = await fetchFPLData();
        const finished = boot?.events
          ?.filter((e) => e.finished || e.data_checked)
          ?.sort((a, b) => b.id - a.id)?.[0];
        const finishedId = Number(finished?.id || 0);
        setLatestFinished(finishedId || null);
        // Elimination does not begin until GW6. Before then, prepare GW6 so the
        // admin can verify the 136-manager Sheet without seeing GW1-5 rules.
        setGameweek((prev) => (prev ? prev : String(finishedId >= 6 ? finishedId : 6)));
      } catch (e) {
        console.error("Failed to detect latest GW:", e);
      }
    })();
  }, []);

  // --- Load rule for this GW + eliminated so far ---
  useEffect(() => {
    (async () => {
      if (!gameweek) return;
      try {
        const r = await fetchEliminationRules(Number(gameweek));
        setRuleCount(r?.eliminate_count ?? 0);
      } catch {
        setRuleCount(0);
      }
      try {
        const list = await fetchEliminatedSoFar(Number(gameweek) - 1);
        setEliminatedSoFar(list || []);
      } catch {
        setEliminatedSoFar([]);
      }
      if (authed) {
        try { setAutoStatus(await fetchEliminationAdminStatus(Number(gameweek))); }
        catch { setAutoStatus(null); }
      }
    })();
  }, [gameweek, authed]);

  // --- Actions ---
  const loadCandidates = async () => {
    if (!leagueId || !gameweek) return;
    setLoading(true);
    try {
      const data = await fetchEliminationCandidates(leagueId.trim(), Number(gameweek), net);
      setRows(data.candidates || []);
      setSuggested(data.suggested || []);
      setSelected(new Set((data.suggested || []).map((x) => x.entry_id)));
    } catch (e) {
      console.error(e);
      setRows([]);
      setSuggested([]);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectNone = () => setSelected(new Set());
  const selectSuggested = () => setSelected(new Set(suggested.map((x) => x.entry_id)));

  const confirm = async () => {
    if (!gameweek) return alert("Select a gameweek.");
    const toSend = rows
      .filter((r) => selected.has(r.entry_id))
      .map((r) => ({
        entry_id: r.entry_id,
        gw_points: r.gw_points,
        season_total: r.season_total ?? 0,
        hits: r.hits ?? 0,
      }));
    if (toSend.length === 0) return alert("Select at least one manager to eliminate.");
    try {
      await confirmEliminations(Number(gameweek), toSend);
      alert(`Eliminated ${toSend.length} manager(s) for GW ${gameweek}.`);
      await loadCandidates(); // refresh
    } catch (e) {
      alert("Failed to confirm eliminations.");
      console.error(e);
    }
  };


  const runAutomatic = async () => {
    if (!gameweek) return;
    setAutoRunning(true);
    try {
      const result = await autoFinalizeElimination(Number(gameweek), leagueId.trim(), false);
      if (result.finalized) alert(result.already_finalized ? `GW ${gameweek} was already finalized.` : `GW ${gameweek}: automatically eliminated ${result.count} manager(s).`);
      else alert(result.reason || "Automatic elimination did not run.");
      setAutoStatus(await fetchEliminationAdminStatus(Number(gameweek)));
      await loadCandidates();
    } catch (e) {
      alert("Automatic finalization failed.");
      console.error(e);
    } finally { setAutoRunning(false); }
  };

  const helpText = useMemo(
    () => (net ? "Sorted by NET points (GW points minus hits)" : "Sorted by raw GW points"),
    [net]
  );

  // --- Locked states ---
  if (checking) {
    return (
      <div className="max-w-md mx-auto mt-10 border rounded p-4">
        <Spinner />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="max-w-md mx-auto mt-10 border rounded p-4">
        <h2 className="text-lg font-semibold mb-2">Admin Access</h2>
        <p className="text-sm text-gray-600 mb-3">
          Enter your admin key to manage eliminations.
        </p>
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            saveAdminKey();
          }}
        >
          <input
            type="password"
            className="border rounded px-3 py-2 w-full"
            placeholder="Paste ADMIN_TOKEN"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            autoComplete="current-password"
            autoFocus
          />
          <button
            type="submit"
            disabled={!adminKey.trim()}
            className="px-3 py-2 rounded bg-black text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Unlock
          </button>
        </form>
        <p className="text-xs text-gray-500 mt-2">
          Tip: The key is stored only in this browser (localStorage).
        </p>
      </div>
    );
  }

  // --- MAIN ADMIN UI (only renders if authed) ---
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-2xl font-bold">Elimination League (Admin)</h1>
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
          Admin verified
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-violet-200 bg-violet-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-violet-950">Automatic elimination is enabled</div>
            <div className="text-sm text-violet-800 mt-1">
              After FPL finalizes a GW, the bottom N managers are selected from Classic League 136323 and saved to Supabase when writes are enabled.
            </div>
          </div>
          <button onClick={runAutomatic} disabled={autoRunning || !gameweek} className="px-4 py-2 rounded-lg bg-violet-700 text-white disabled:opacity-50">
            {autoRunning ? "Checking…" : "Run / Repair Auto Finalize"}
          </button>
        </div>
        <div className="grid sm:grid-cols-4 gap-2 mt-3 text-xs">
          <div><b>League 136323:</b> {autoStatus?.roster?.valid ? `${autoStatus.roster.count} managers ✓` : (autoStatus?.roster?.count != null ? `${autoStatus.roster.count} managers (expected 136)` : (autoStatus?.roster?.error || "Checking…"))}</div>
          <div><b>FPL GW:</b> {autoStatus?.fpl_finalized ? "Finalized ✓" : "Not finalized"}</div>
          <div><b>Rule:</b> eliminate {autoStatus?.rule_count ?? ruleCount}</div>
          <div><b>Saved:</b> {autoStatus?.eliminated_count ?? 0} · <b>Writes:</b> {autoStatus?.automatic ? "Enabled" : "Disabled"}</div>
        </div>
        <div className="text-xs text-violet-700 mt-3">
          Tie-break order: 1) more goals scored survives, 2) if still tied, more goals conceded is eliminated first, 3) more assists survives, 4) overall FPL rank is only the final fallback.
        </div>
      </div>

      {/* Controls */}
      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="text-sm">Elimination League ID</label>
          <input
            className="border rounded w-full px-3 py-2"
            value={leagueId}
            onChange={(e) => setLeagueId(e.target.value)}
            placeholder="e.g. 136323"
          />
        </div>

        <div>
          <label className="text-sm">Gameweek</label>
          <input
            className="border rounded w-full px-3 py-2"
            type="number"
            min={1}
            value={gameweek}
            onChange={(e) => setGameweek(e.target.value)}
            placeholder={latestFinished && latestFinished >= 6 ? `Latest Elimination GW: ${latestFinished}` : "Starts at GW6"}
          />
        </div>

        <div className="flex items-end gap-2 flex-wrap">
          <button
            onClick={loadCandidates}
            className="px-4 py-2 rounded bg-black text-white"
          >
            {loading ? "Loading…" : "Load Managers"}
          </button>

          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={net} onChange={() => setNet(!net)} />
            Use NET points
          </label>

          <button
            onClick={confirm}
            disabled={selectedCount === 0}
            className={`px-4 py-2 rounded ${
              selectedCount === 0
                ? "bg-red-400 cursor-not-allowed text-white"
                : "bg-red-600 text-white"
            }`}
            title={selectedCount === 0 ? "Select at least one manager to enable" : ""}
          >
            Confirm Elimination{selectedCount ? ` (${selectedCount})` : ""}
          </button>
        </div>
      </div>

      {/* Rule & Eliminated so far */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="border rounded p-3 bg-slate-50">
          <div className="font-medium">Fixed roadmap · GW {gameweek || "-"}</div>
          <div className="mt-2 text-2xl font-extrabold text-violet-700">Eliminate {ruleCount}</div>
          <div className="text-xs text-gray-500 mt-2">
            Season 16 schedule is fixed: GW6–11 = 5/GW, GW12–18 = 5/GW, GW19–27 = 4/GW, GW28–37 = 3/GW, GW38 = 4. No manual rule setup is required.
          </div>
        </div>

        <div className="border rounded p-3">
          <div className="font-medium">Eliminated before GW {gameweek || "-"}</div>
          <div className="text-sm mt-2 max-h-36 overflow-auto">
            {eliminatedSoFar?.length ? (
              <ul className="list-disc pl-5">
                {eliminatedSoFar.map((r) => (
                  <li key={`${r.gw}-${r.entry_id}`}>
                    GW {r.gw} — Entry {r.entry_id} (pts {r.gw_points}
                    {typeof r.hits === "number" ? `, hits ${r.hits}` : ""})
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-gray-500">None</span>
            )}
          </div>
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-2">
        {helpText}. Tie-break: goals scored → goals conceded → assists → overall rank fallback.
      </p>

      {/* Table */}
      <div className="overflow-x-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">#</th>
              <th className="p-2 text-left">Elim</th>
              <th className="p-2 text-left">Entry</th>
              <th className="p-2 text-left">Manager</th>
              <th className="p-2 text-left">Team</th>
              <th className="p-2 text-right">GW pts</th>
              <th className="p-2 text-right">Hits</th>
              <th className="p-2 text-right">NET</th>
              <th className="p-2 text-right">Goals</th>
              <th className="p-2 text-right">Conceded</th>
              <th className="p-2 text-right">Assists</th>
              <th className="p-2 text-right">Season Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td className="p-3 text-center" colSpan={12}>
                  No data yet. Click “Load Managers”.
                </td>
              </tr>
            )}
            {rows.map((r, idx) => (
              <tr key={r.entry_id} className={selected.has(r.entry_id) ? "bg-red-50" : ""}>
                <td className="p-2">{idx + 1}</td>
                <td className="p-2">
                  <input
                    type="checkbox"
                    checked={selected.has(r.entry_id)}
                    onChange={() => toggleSelected(r.entry_id)}
                  />
                </td>
                <td className="p-2">{r.entry_id}</td>
                <td className="p-2">{r.manager_name}</td>
                <td className="p-2">{r.team_name}</td>
                <td className="p-2 text-right">{r.gw_points}</td>
                <td className="p-2 text-right">{r.hits}</td>
                <td className="p-2 text-right">{r.net_points}</td>
                <td className="p-2 text-right">{r.goals_scored ?? 0}</td>
                <td className="p-2 text-right">{r.goals_conceded ?? 0}</td>
                <td className="p-2 text-right">{r.assists ?? 0}</td>
                <td className="p-2 text-right">{r.season_total ?? "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button onClick={selectSuggested} className="px-3 py-1 rounded border">
          Select Suggested
        </button>
        <button onClick={selectNone} className="px-3 py-1 rounded border">
          Clear Selection
        </button>
      </div>
    </div>
  );
}
